// Mirrors iOS RestaurantNotificationRowView: type icon in a tinted circle,
// title + relative time, 2-line message, unread dot (brand orange), and
// trailing actions replacing iOS swipe actions (mark read / delete).
import {
  ShoppingBag, PlusCircle, Car, MapPin, CheckCircle2, Star, XCircle,
  Gift, Settings, Trash2, Check,
} from 'lucide-react';
import { timeAgo } from '../../shared/models';

export const TYPE_STYLE = {
  order_update:        { Icon: ShoppingBag,  fg: 'text-blue-500',   bg: 'bg-blue-50' },
  order_status:        { Icon: ShoppingBag,  fg: 'text-blue-500',   bg: 'bg-blue-50' },
  new_order:           { Icon: PlusCircle,   fg: 'text-blue-500',   bg: 'bg-blue-50' },
  order_confirmed:     { Icon: ShoppingBag,  fg: 'text-blue-500',   bg: 'bg-blue-50' },
  order_preparing:     { Icon: ShoppingBag,  fg: 'text-blue-500',   bg: 'bg-blue-50' },
  driver_assigned:     { Icon: Car,          fg: 'text-green-600',  bg: 'bg-green-50' },
  driver_accepted:     { Icon: Car,          fg: 'text-green-600',  bg: 'bg-green-50' },
  driver_picked_up:    { Icon: Car,          fg: 'text-green-600',  bg: 'bg-green-50' },
  order_picked_up:     { Icon: Car,          fg: 'text-green-600',  bg: 'bg-green-50' },
  driver_on_the_way:   { Icon: Car,          fg: 'text-green-600',  bg: 'bg-green-50' },
  order_on_the_way:    { Icon: Car,          fg: 'text-green-600',  bg: 'bg-green-50' },
  delivery_update:     { Icon: Car,          fg: 'text-green-600',  bg: 'bg-green-50' },
  driver_nearby:       { Icon: MapPin,       fg: 'text-[#FF6B35]',  bg: 'bg-orange-50' },
  driver_arrived:      { Icon: MapPin,       fg: 'text-[#FF6B35]',  bg: 'bg-orange-50' },
  driver_arrived_customer: { Icon: MapPin,   fg: 'text-[#FF6B35]',  bg: 'bg-orange-50' },
  order_ready:         { Icon: CheckCircle2, fg: 'text-[#FF6B35]',  bg: 'bg-orange-50' },
  order_completed:     { Icon: Star,         fg: 'text-green-600',  bg: 'bg-green-50' },
  delivery_completed:  { Icon: Star,         fg: 'text-green-600',  bg: 'bg-green-50' },
  order_delivered:     { Icon: Star,         fg: 'text-green-600',  bg: 'bg-green-50' },
  order_cancelled:     { Icon: XCircle,      fg: 'text-red-500',    bg: 'bg-red-50' },
  promotion:           { Icon: Gift,         fg: 'text-purple-500', bg: 'bg-purple-50' },
  system:              { Icon: Settings,     fg: 'text-gray-500',   bg: 'bg-gray-100' },
};

export default function NotificationRow({ notification, onTap, onMarkRead, onDelete }) {
  const style = TYPE_STYLE[notification.type] || TYPE_STYLE.order_update;
  const { Icon } = style;
  // Admin broadcasts (promotion / data.broadcast) show the CheF logo.
  const isBroadcast = notification.type === 'promotion' || notification.data?.broadcast === 'true';

  return (
    <div
      onClick={onTap}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onTap?.()}
      className={`w-full flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-colors active:bg-gray-100 ${
        notification.isRead ? 'bg-gray-50/60' : 'bg-white shadow-sm border border-gray-100'
      }`}
    >
      {isBroadcast ? (
        <img src="/ChefLogo.jpg" alt="CheF SKN" className="w-10 h-10 rounded-full object-cover shrink-0 bg-white" />
      ) : (
        <span className={`w-10 h-10 rounded-full shrink-0 flex items-center justify-center ${style.bg} ${style.fg}`}>
          <Icon size={18} />
        </span>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-[15px] leading-snug truncate ${notification.isRead ? 'font-medium text-gray-700' : 'font-semibold text-gray-900'}`}>
            {notification.title || 'Notification'}
          </p>
          <span className="text-[11px] text-gray-400 shrink-0 pt-0.5">{timeAgo(notification.createdAt)}</span>
        </div>
        <p className="text-sm text-gray-500 line-clamp-2 mt-0.5">{notification.message}</p>
        {notification.orderId && (
          <p className="text-[11px] text-blue-500 mt-1">Order #{String(notification.orderId).slice(0, 8)} · tap to view</p>
        )}
      </div>

      <div className="flex flex-col items-center gap-2 shrink-0 pt-1">
        {/* Unread dot — brand orange per spec (iOS uses blue) */}
        <span className={`w-2 h-2 rounded-full ${notification.isRead ? 'bg-transparent' : 'bg-[#FF6B35]'}`} />
        {!notification.isRead && (
          <button
            onClick={(e) => { e.stopPropagation(); onMarkRead?.(); }}
            className="w-7 h-7 rounded-full bg-blue-50 text-blue-500 flex items-center justify-center active:scale-95"
            title="Mark as read"
          >
            <Check size={14} />
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
          className="w-7 h-7 rounded-full bg-red-50 text-red-500 flex items-center justify-center active:scale-95"
          title="Delete"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}
